import { useTranslation } from "react-i18next";

import type { ActionResult } from "shell/api/types";

import { translateError } from "common-services/utils/passwordErrors";

interface UseForgotPasswordFlowArgs {
  confirmPasswordReset: (
    username: string,
    code: string,
    newPassword: string,
  ) => Promise<ActionResult>;
  forgotConfirmUsername: string | undefined;
  onConfirmed: () => void;
  onRequested: (email: string) => void;
  requestPasswordReset: (email: string) => Promise<ActionResult>;
  setEmailHint: (email: string) => void;
  setError: (message: string) => void;
  wrapSubmit: <T>(
    fn: (values: T) => Promise<void>,
  ) => (values: T) => Promise<void>;
}

export const useForgotPasswordFlow = ({
  requestPasswordReset,
  confirmPasswordReset,
  forgotConfirmUsername,
  wrapSubmit,
  setEmailHint,
  setError,
  onRequested,
  onConfirmed,
}: UseForgotPasswordFlowArgs) => {
  const { t } = useTranslation(["common"]);

  const submitForgotPassword = wrapSubmit(
    async ({ email }: { email: string }) => {
      setEmailHint(email);
      const result = await requestPasswordReset(email);
      if (result.kind === "ok") {
        onRequested(email);
      } else {
        setError(translateError(result.code, result.message, t));
      }
    },
  );

  const submitForgotConfirm = wrapSubmit(
    async ({ code, newPassword }: { code: string; newPassword: string }) => {
      if (!forgotConfirmUsername) return;
      const result = await confirmPasswordReset(
        forgotConfirmUsername,
        code,
        newPassword,
      );
      if (result.kind === "ok") {
        onConfirmed();
      } else {
        setError(translateError(result.code, result.message, t));
      }
    },
  );

  return { submitForgotPassword, submitForgotConfirm };
};
